if (!customElements.get('cart-threshold-bar')) {
  // Shared across every <cart-threshold-bar> instance (the theme's own cart
  // re-render can mount a fresh instance mid-flight, which would otherwise
  // bypass a per-instance _busy flag and trigger a duplicate /cart/add call
  // for the same gift — Shopify then merges the two lines into quantity 2).
  let globalGiftLock = false;

  customElements.define(
    'cart-threshold-bar',
    class CartThresholdBar extends HTMLElement {
      constructor() {
        super();
        this._unsubscribe = null;
      }

      connectedCallback() {
        try {
          this.config = JSON.parse(this.dataset.config || '{}');
        } catch (e) {
          console.error('[threshold-bar] invalid data-config', e);
          this.config = { tiers: [], effective_total_cents: 0 };
        }

        this._onCartUpdate = (event) => this.onCartUpdate(event);
        document.addEventListener('cart:update', this._onCartUpdate);

        this.bindGiftForm();
        this.reconcileGifts();
      }

      disconnectedCallback() {
        document.removeEventListener('cart:update', this._onCartUpdate);
      }

      onCartUpdate(event) {
        if (event && event.detail && event.detail.source === 'threshold-bar') return;
        this.reconcileGifts(null);
      }

      async reconcileGifts(cartData) {
        if (globalGiftLock) return;
        globalGiftLock = true;
        try {
          const allTiers = this.config.tiers || [];
          if (!allTiers.length) return;

          const cart =
            cartData ||
            (await fetch(`${window.routes ? window.routes.cart_url : '/cart'}.js`).then((r) => r.json()));

          // Index gift lines by tier marker. Also compute effective total
          // (cart total minus any auto-added gift line totals) — matches Liquid logic.
          const giftLinesByTier = new Map();
          let giftTotalCents = 0;
          for (const line of cart.items || []) {
            const marker = line.properties && line.properties._threshold_gift_tier;
            if (marker != null && marker !== '') {
              giftLinesByTier.set(String(marker), line);
              giftTotalCents += line.line_price || 0;
            }
          }
          let effectiveTotalCents = (cart.total_price || 0) - giftTotalCents;
          if (effectiveTotalCents < 0) effectiveTotalCents = 0;

          // Map tier index → tier config for fast lookup
          const tierByIndex = new Map(allTiers.map((t) => [String(t.index), t]));

          const toRemove = [];
          // Remove any gift line whose tier no longer qualifies (or whose tier was deleted).
          for (const [tierKey, line] of giftLinesByTier.entries()) {
            const tier = tierByIndex.get(tierKey);
            if (!tier || effectiveTotalCents < tier.amount_cents) {
              toRemove.push(line);
            }
          }

          // Collect product IDs already present in the cart (regardless of how
          // they got there) so we don't auto-add a gift the customer already has.
          const productIdsInCart = new Set();
          for (const line of cart.items || []) {
            if (line.product_id) productIdsInCart.add(line.product_id);
          }

          // Add auto-addable gifts (variant_id known) that qualify and aren't present.
          const toAdd = [];
          for (const tier of allTiers) {
            if (!tier.variant_id || tier.variant_id <= 0) continue; // claim-card tier
            const tierKey = String(tier.index);
            if (giftLinesByTier.has(tierKey)) continue;
            if (tier.product_id && productIdsInCart.has(tier.product_id)) continue;
            if (effectiveTotalCents >= tier.amount_cents) toAdd.push(tier);
          }

          if (!toAdd.length && !toRemove.length) {
            await this.refreshSelf();
            return;
          }

          for (const line of toRemove) {
            await this.removeLineByKey(line.key);
          }
          for (const tier of toAdd) {
            await this.addGift(tier.variant_id, tier.index);
          }

          await this.clampDuplicateGifts();
          await this.broadcastCartChange();
        } catch (e) {
          console.error('[threshold-bar] reconcile failed', e);
        } finally {
          globalGiftLock = false;
        }
      }

      // Safety net: if a race ever slips past the lock and a gift line ends up
      // with quantity > 1, force it back down to 1 so the customer never gets
      // more free units than the tier grants.
      async clampDuplicateGifts() {
        try {
          const base = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || (window.routes && window.routes.cart_url) || '/cart';
          const cart = await fetch(`${base}.js`).then((r) => r.json());
          for (const line of cart.items || []) {
            const marker = line.properties && line.properties._threshold_gift_tier;
            if (marker != null && marker !== '' && line.quantity > 1) {
              await this.setLineQuantity(line.key, 1);
            }
          }
        } catch (e) {
          console.error('[threshold-bar] clampDuplicateGifts failed', e);
        }
      }

      setLineQuantity(key, quantity) {
        const body = { id: key, quantity };
        const url = (window.Theme && window.Theme.routes && window.Theme.routes.cart_change_url) || (window.routes && window.routes.cart_change_url) || '/cart/change';
        return fetch(`${url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
      }

      // Publish cart-update for the rest of the theme (refreshes cart-drawer-items,
      // .cart-drawer__footer, cart-icon-bubble via their subscribers) then refetches
      // the cart-drawer section to swap our own <cart-threshold-bar> element —
      // the existing onCartUpdate handlers don't include the threshold bar in
      // their refresh selectors, so the bar would otherwise stay stale.
      async broadcastCartChange() {
        // Fetch fresh section HTML and pass it directly in the event so the
        // cart-items component morphs immediately without a second round-trip.
        try {
          const base = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || (window.routes && window.routes.cart_url) || '/cart';
          const sections = await fetch(`${base}?sections=cart-products`)
            .then((r) => r.json())
            .catch(() => null);
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { source: 'threshold-bar', data: { sections: sections || {} } }
          }));
        } catch (e) {
          document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { source: 'threshold-bar' } }));
        }
      }

      async refreshSelf() {
        try {
          const base = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || (window.routes && window.routes.cart_url) || '/cart';
          const html = await fetch(`${base}?sections=cart-products`).then((r) => r.json()).then(data => data['cart-products'] || '').catch(() => fetch(`${base}?section_id=cart-products`).then(r => r.text()));
          const doc = new DOMParser().parseFromString(typeof html === 'string' ? html : '', 'text/html');
          const newBar = doc.querySelector('cart-threshold-bar');
          if (newBar && this.parentNode) {
            // Update in-place so the same element/listener stays in the DOM.
            // Replacing with replaceWith() causes connectedCallback on the new
            // element which re-enters reconcileGifts immediately, creating an
            // infinite refresh loop and dropping future cart:update listeners.
            this.dataset.config = newBar.dataset.config;
            this.innerHTML = newBar.innerHTML;
            try { this.config = JSON.parse(this.dataset.config || '{}'); } catch (_) {}
          }
        } catch (e) {
          console.error('[threshold-bar] refreshSelf failed', e);
        }
      }

      addGift(variantId, tierIndex) {
        const body = {
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: { _threshold_gift_tier: String(tierIndex) },
            },
          ],
        };
        const url = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || (window.routes && window.routes.cart_add_url) || '/cart/add';
        return fetch(`${url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        }).then((r) => {
          if (!r.ok) return r.json().then((err) => Promise.reject(err));
          return r.json();
        });
      }

      removeLineByKey(key) {
        const body = { id: key, quantity: 0 };
        const url = (window.Theme && window.Theme.routes && window.Theme.routes.cart_change_url) || (window.routes && window.routes.cart_change_url) || '/cart/change';
        return fetch(`${url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
      }

      bindGiftForm() {
        const card = this.querySelector('[data-threshold-gift-card]');
        if (!card) return;
        const form = card.querySelector('[data-threshold-gift-form]');
        if (!form) return;

        let variants;
        try {
          variants = JSON.parse(card.dataset.variants || '[]');
        } catch (e) {
          console.error('[threshold-bar] invalid variants data', e);
          return;
        }
        const tierIndex = card.dataset.tierIndex;

        form.addEventListener('submit', async (event) => {
          event.preventDefault();
          if (globalGiftLock) return;

          const submitBtn = form.querySelector('[data-threshold-gift-submit]');
          const selects = form.querySelectorAll('[data-option-position]');
          const selectedOptions = Array.from(selects)
            .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
            .map((s) => s.value);

          const match = variants.find(
            (v) =>
              v.available &&
              v.options.length === selectedOptions.length &&
              v.options.every((opt, i) => opt === selectedOptions[i])
          );

          if (!match) {
            submitBtn.classList.add('error');
            return;
          }

          submitBtn.setAttribute('disabled', 'disabled');
          submitBtn.classList.add('loading');
          globalGiftLock = true;
          try {
            await this.addGift(match.id, tierIndex);
            await this.clampDuplicateGifts();
            await this.broadcastCartChange();
          } catch (e) {
            console.error('[threshold-bar] manual claim failed', e);
            submitBtn.removeAttribute('disabled');
            submitBtn.classList.remove('loading');
          } finally {
            globalGiftLock = false;
          }
        });
      }
    }
  );
}
